import { useEffect, useMemo, useRef, useState } from "react";
import Topbar from "@/components/Topbar";
import { idSuffix } from "@/lib/idLabel";
import { formatDate, formatDateTime } from "@/lib/dates";
import { fmtMB } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Download,
  Recycle,
  Truck,
  AlertTriangle,
  Building2,
  Layers,
  Image as ImageIcon,
  X as XIcon,
  Cog,
  Package,
  ArrowUpRight,
  Plus,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { HistoryDetailDialog, getShipmentReviewState } from "@/components/HistoryDetailDialog";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from "recharts";
import { useLiveRefresh } from "@/hooks/useLiveRefresh";

const OUTCOME_COLORS = {
  Recycler: "#056839",
  Converter: "#69A488",
  Landfill: "#3F3F3F",
  Incinerator: "#A0522D",
};
const DEFAULT_COLORS = ["#056839", "#69A488", "#A0522D", "#E4D5B7", "#D2E4D6", "#3F3F3F"];

const CHANNEL_NAMES = {
  everlantern: "Everlantern",
  hong_zheng: "Hong Zheng",
  newgates: "Newgates",
  hl: "H&L",
};
const CHANNEL_ACCENTS = {
  everlantern: { border: "#056839", tint: "#D2E4D6", text: "#056839" },
  hong_zheng:  { border: "#A0522D", tint: "#E4D5B7", text: "#A0522D" },
  newgates:    { border: "#69A488", tint: "#D2E4D6", text: "#056839" },
  hl:          { border: "#3F3F3F", tint: "#E4D5B7", text: "#3F3F3F" },
};

function RecordId({ id }) {
  if (!id) return null;
  return (
    <span
      className="text-[10px] font-mono text-[#3F3F3F]/40 select-all"
      title={id}
      data-testid={`record-id-${id}`}
    >
      ID: {String(id).slice(0, 8)}…
    </span>
  );
}

export default function AdminDashboard() {
  const [report, setReport] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [activeDetail, setActiveDetail] = useState(null); // "collected" | "received" | "processed" | "shipped" | null
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersOpen, setUsersOpen] = useState(false);
  const [adminTab, setAdminTab] = useState("overview"); // "overview" | "users"
  const [pendingShipmentCount, setPendingShipmentCount] = useState(0);
  const [showPendingShipDetail, setShowPendingShipDetail] = useState(false);
  const [channelDetailFilter, setChannelDetailFilter] = useState(null); // { channelId, initialKind, status?, admin_status? }

  useEffect(() => {
    loadReport();
    loadUsers();
    loadShipmentsCount();
    // Fetchers are stable closures defined in scope; depending on them would
    // refetch on every render.
  }, [from, to]);

  useLiveRefresh(() => {
    loadReport();
    loadUsers();
    loadShipmentsCount();
  }, { intervalMs: 20000 });

  async function loadShipmentsCount() {
    try {
      const { data } = await api.get("/admin/shipments");
      setPendingShipmentCount(
        (data || []).filter((s) => (s.admin_status || "pending") === "pending").length
      );
    } catch { /* ignore */ }
  }

  async function loadUsers() {
    try {
      const { data } = await api.get("/admin/users");
      setAllUsers(data);
      setPendingUsers(data.filter((u) => u.status === "Pending"));
    } catch { /* ignore */ }
  }

  async function loadReport() {
    try {
      const params = {};
      if (from) params.from = new Date(from).toISOString();
      if (to) params.to = new Date(to).toISOString();
      const { data } = await api.get("/admin/reports", { params });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  function buildRangeParams() {
    const p = {};
    if (from) p.from = new Date(from).toISOString();
    if (to) p.to = new Date(to).toISOString();
    return p;
  }

  async function exportCsv(kind, extra = {}) {
    try {
      const res = await api.get("/admin/export.csv", {
        params: { kind, ...buildRangeParams(), ...extra },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `process_${kind}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Topbar subtitle="Admin" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 text-sm text-[#3F3F3F]">
          Loading reports…
        </div>
      </div>
    );
  }

  const { totals, by_material, by_outcome, by_facility } = report;
  const recoveryPct = (totals.recovery_rate * 100).toFixed(1);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Topbar subtitle="Admin" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="admin-dashboard">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">PRO console</div>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-[#000000] mt-1">
              Traceability overview
            </h1>
            <p className="text-sm text-[#3F3F3F] mt-1">
              Click any metric for the full log. Filter by period and export anytime.
            </p>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                From
              </label>
              <Input
                type="datetime-local"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="admin-from"
                className="h-10 mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">
                To
              </label>
              <Input
                type="datetime-local"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="admin-to"
                className="h-10 mt-1"
              />
            </div>
            {(from || to) && (
              <button
                onClick={() => {
                  setFrom("");
                  setTo("");
                }}
                className="h-10 text-xs underline text-[#3F3F3F]/70 hover:text-[#056839]"
                data-testid="admin-clear-range"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Top-level tabs */}
        <div className="flex items-center gap-1 border-b border-[#D2E4D6]" data-testid="admin-tabs">
          {[
            { v: "overview", l: "Overview" },
            { v: "users", l: `Users${pendingUsers.length ? ` (${pendingUsers.length})` : ""}` },
          ].map((t) => (
            <button key={t.v}
              onClick={() => setAdminTab(t.v)}
              data-testid={`admin-tab-${t.v}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                adminTab === t.v
                  ? "border-[#056839] text-[#056839]"
                  : "border-transparent text-[#3F3F3F]/70 hover:text-[#056839]"
              }`}>
              {t.l}
            </button>
          ))}
        </div>

        {adminTab === "users" && (
          <>
            <AddUserCard onCreated={loadUsers} pending={pendingUsers} allUsers={allUsers} />
            <UsersTable users={allUsers} onChanged={loadUsers} />
          </>
        )}

        {adminTab === "overview" && (
        <>
        {/* Stat row — clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <StatCard
            label="Collected"
            value={`${totals.collected_kg.toFixed(0)} kg`}
            sub={`${totals.collected_count} collections`}
            icon={<Truck className="w-5 h-5" />}
            onClick={() => setActiveDetail("collected")}
            testId="metric-total-collected"
          />
          <StatCard
            label="Received"
            value={`${totals.received_kg.toFixed(0)} kg`}
            sub={`${totals.received_count} confirmed · ${totals.rejected_count} rejected`}
            icon={<Layers className="w-5 h-5" />}
            tint="#D2E4D6"
            color="#056839"
            onClick={() => setActiveDetail("received")}
            testId="metric-total-received"
          />
          <StatCard
            label="Processed"
            value={`${totals.processed_kg.toFixed(0)} kg`}
            sub={`${totals.pending_count} pending`}
            icon={<Cog className="w-5 h-5" />}
            tint="#E4D5B7"
            color="#A0522D"
            onClick={() => setActiveDetail("processed")}
            testId="metric-total-processed"
          />
          <StatCard
            label="Moved to end destination"
            value={`${recoveryPct}%`}
            sub={`${totals.shipped_kg.toFixed(0)} kg shipped`}
            icon={<Recycle className="w-5 h-5" />}
            tint="#056839"
            color="#FFFFFF"
            onClick={() => setActiveDetail("shipped")}
            testId="metric-recovery-rate"
          />
        </div>

        {/* Pending shipment reviews KPI */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <button
            type="button"
            onClick={() => setShowPendingShipDetail(true)}
            data-testid="admin-kpi-pending-shipments"
            className={`text-left rounded-xl p-5 border shadow-sm transition-all hover:shadow-md ${
              pendingShipmentCount > 0
                ? "border-[#A0522D] bg-[#A0522D]/5"
                : "border-[#D2E4D6]/60 bg-white opacity-80"
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className={`text-xs uppercase tracking-[0.15em] font-medium ${pendingShipmentCount > 0 ? "text-[#A0522D]" : "text-[#69A488]"}`}>
                  Pending shipment reviews
                </div>
                <div className={`font-heading text-3xl font-semibold tracking-tight mt-2 ${pendingShipmentCount > 0 ? "text-[#A0522D]" : "text-[#3F3F3F]/50"}`}>
                  {pendingShipmentCount}
                </div>
                <div className="text-xs text-[#3F3F3F]/70 mt-1">
                  {pendingShipmentCount > 0 ? "Click to review" : "All reviewed"}
                </div>
              </div>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pendingShipmentCount > 0 ? "bg-[#A0522D]/15 text-[#A0522D]" : "bg-[#D2E4D6] text-[#69A488]"}`}>
                <AlertTriangle className="w-5 h-5" />
              </div>
            </div>
          </button>
        </div>

        {/* Per-channel KPI breakdown */}
        <ChannelOverviewSection
          rangeParams={buildRangeParams()}
          onChannelDrill={(payload) => setChannelDetailFilter(payload)}
        />

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-[#D2E4D6]/80 rounded-xl">
            <CardContent className="p-5 md:p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">By material</div>
              <h3 className="font-heading text-lg font-semibold text-[#000000] mb-1">
                Material breakdown (kg)
              </h3>
              <p className="text-xs text-[#3F3F3F]/70 mb-3">
                Collector-declared = collector-declared weight (all statuses) · Received = facility-confirmed weight (accepted deliveries only)
              </p>
              <div className="h-64" data-testid="chart-by-material">
                {by_material.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={by_material} barGap={4} barCategoryGap="24%">
                      <XAxis dataKey="material_type" stroke="#3F3F3F" tick={{ fill: "#3F3F3F", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis stroke="#3F3F3F" tick={{ fill: "#3F3F3F", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <RTooltip
                        contentStyle={{ background: "#fff", border: "1px solid #D2E4D6", borderRadius: 8 }}
                        formatter={(value, name) => [`${Number(value).toLocaleString()} kg`, name]}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        iconType="circle"
                      />
                      <Bar
                        dataKey="offered_kg"
                        name="Collector-declared"
                        fill="#A0522D"
                        radius={[4, 4, 0, 0]}
                        data-testid="bar-collector-declared"
                      />
                      <Bar
                        dataKey="received_kg"
                        name="Received (accepted)"
                        fill="#056839"
                        radius={[4, 4, 0, 0]}
                        data-testid="bar-received"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-[#D2E4D6]/80 rounded-xl">
            <CardContent className="p-5 md:p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">By outcome</div>
              <h3 className="font-heading text-lg font-semibold text-[#000000] mb-3">
                Shipment destinations
              </h3>
              <div className="h-64" data-testid="chart-by-outcome">
                {by_outcome.length === 0 ? (
                  <EmptyChart />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={by_outcome} dataKey="kg" nameKey="destination_type" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                        {by_outcome.map((e, i) => (
                          <Cell key={e.destination_type || `cell-${i}`} fill={OUTCOME_COLORS[e.destination_type] || DEFAULT_COLORS[i % DEFAULT_COLORS.length]} />
                        ))}
                      </Pie>
                      <RTooltip contentStyle={{ background: "#fff", border: "1px solid #D2E4D6", borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="flex flex-wrap gap-3 mt-3">
                {by_outcome.map((o) => (
                  <div key={o.destination_type} className="flex items-center gap-1.5 text-xs">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: OUTCOME_COLORS[o.destination_type] || "#69A488" }} />
                    <span className="text-[#3F3F3F]">{o.destination_type}</span>
                    <span className="font-mono text-[#056839] font-semibold">{o.kg.toFixed(0)} kg</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Historical records (persistent panel — same shape as facility console) */}
        <AdminHistoryPanel rangeParams={buildRangeParams()} />
        </>
        )}
      </div>

      {/* Detail dialog */}
      <DetailDialog
        kind={activeDetail}
        onClose={() => setActiveDetail(null)}
        rangeParams={buildRangeParams()}
      />
      <DetailDialog
        kind={showPendingShipDetail ? "shipped" : null}
        onClose={() => setShowPendingShipDetail(false)}
        rangeParams={buildRangeParams()}
        initialFilters={{ admin_status: "pending" }}
      />
      <DetailDialog
        kind={channelDetailFilter ? (channelDetailFilter.initialKind || "collected") : null}
        onClose={() => setChannelDetailFilter(null)}
        rangeParams={buildRangeParams()}
        initialFilters={{
          channel_id: channelDetailFilter?.channelId || "",
          ...(channelDetailFilter?.status ? { status: channelDetailFilter.status } : {}),
          ...(channelDetailFilter?.admin_status ? { admin_status: channelDetailFilter.admin_status } : {}),
        }}
        allowKindSwitch={!!channelDetailFilter}
        title={channelDetailFilter ? `${CHANNEL_NAMES[channelDetailFilter.channelId] || channelDetailFilter.channelId} — channel records` : undefined}
      />
    </div>
  );
}

/* ------- ChannelOverviewSection (per-channel KPI cards) ------- */
function ChannelOverviewSection({ onChannelDrill, rangeParams = {} }) {
  const [collections, setCollections] = useState([]);
  const [shipments, setShipments] = useState([]);
  const rangeKey = `${rangeParams.from || ""}|${rangeParams.to || ""}`;
  const isFiltered = Boolean(rangeParams.from || rangeParams.to);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/admin/collections", { params: rangeParams }),
          api.get("/admin/shipments",   { params: rangeParams }),
        ]);
        setCollections(c.data || []);
        setShipments(s.data || []);
      } catch { /* ignore */ }
    })();
    // Refetch only when the date range changes; effective range is what we
    // care about, the params object would create infinite render loops.
  }, [rangeKey]);

  const stats = useMemo(() => {
    const out = {};
    Object.keys(CHANNEL_NAMES).forEach((k) => {
      out[k] = { coll_count: 0, kg_coll: 0, ship_count: 0, kg_ship: 0, pending_coll: 0, pending_ship: 0 };
    });
    collections.forEach((c) => {
      const k = c.channel_id;
      if (!out[k]) return;
      out[k].coll_count += 1;
      out[k].kg_coll += (c.received_weight_kg || c.estimated_weight_kg || 0);
      if (c.status === "Pending") out[k].pending_coll += 1;
    });
    shipments.forEach((s) => {
      const k = s.channel_id;
      if (!out[k]) return;
      out[k].ship_count += 1;
      out[k].kg_ship += (s.total_shipped_kg || 0);
      if ((s.admin_status || "pending") === "pending") out[k].pending_ship += 1;
    });
    return out;
  }, [collections, shipments]);

  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="channel-overview-section">
      <CardContent className="p-5 md:p-6">
        <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Channel overview</div>
        <h3 className="font-heading text-lg font-semibold text-[#000000]">
          Performance per channel{isFiltered && <span className="text-sm font-normal text-[#3F3F3F]/60"> · filtered</span>}
        </h3>
        <p className="text-xs text-[#3F3F3F]/70 mt-0.5 mb-4">
          Click the channel name to see every record, or click any tile below to drill into that specific list.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(CHANNEL_NAMES).map(([id, name]) => {
            const a = CHANNEL_ACCENTS[id];
            const s = stats[id];
            const needsAction = s.pending_coll + s.pending_ship > 0;
            return (
              <div
                key={id}
                data-testid={`channel-card-${id}`}
                className="rounded-xl border bg-white p-5 hover:shadow-md transition-all"
                style={{ borderColor: a.border + "55", borderLeftWidth: "4px", borderLeftColor: a.border }}
              >
                <div className="flex items-start justify-between mb-3 gap-2">
                  <button
                    type="button"
                    onClick={() => onChannelDrill && onChannelDrill({ channelId: id, initialKind: "collected" })}
                    data-testid={`channel-card-${id}-title`}
                    className="text-left font-heading text-lg font-semibold hover:underline"
                    style={{ color: a.text }}
                  >
                    {name}
                  </button>
                  {needsAction && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#A0522D]/15 text-[#A0522D]" data-testid={`channel-card-${id}-needs-action`}>
                      Action needed
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat
                    label="Collections"
                    value={s.coll_count}
                    sub={`${s.kg_coll.toFixed(0)} kg`}
                    tint={a.tint} text={a.text}
                    onClick={() => onChannelDrill && onChannelDrill({ channelId: id, initialKind: "collected" })}
                    testId={`channel-${id}-collections`}
                  />
                  <Stat
                    label="Shipments"
                    value={s.ship_count}
                    sub={`${s.kg_ship.toFixed(0)} kg`}
                    tint={a.tint} text={a.text}
                    onClick={() => onChannelDrill && onChannelDrill({ channelId: id, initialKind: "shipped" })}
                    testId={`channel-${id}-shipments`}
                  />
                  <Stat
                    label="Pending collections"
                    value={s.pending_coll}
                    sub={s.pending_coll > 0 ? "Awaiting review" : "—"}
                    tint={s.pending_coll > 0 ? "#E4D5B7" : a.tint}
                    text={s.pending_coll > 0 ? "#A0522D" : a.text}
                    onClick={() => onChannelDrill && onChannelDrill({ channelId: id, initialKind: "collected", status: "Pending" })}
                    testId={`channel-${id}-pending-coll`}
                  />
                  <Stat
                    label="Pending shipments"
                    value={s.pending_ship}
                    sub={s.pending_ship > 0 ? "Awaiting approval" : "—"}
                    tint={s.pending_ship > 0 ? "#E4D5B7" : a.tint}
                    text={s.pending_ship > 0 ? "#A0522D" : a.text}
                    onClick={() => onChannelDrill && onChannelDrill({ channelId: id, initialKind: "shipped", admin_status: "pending" })}
                    testId={`channel-${id}-pending-ship`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, tint, text, testId, onClick }) {
  const base = "rounded-lg p-3 text-left w-full transition-all";
  const interactive = onClick ? "hover:ring-2 hover:ring-[#056839]/30 cursor-pointer" : "";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`${base} ${interactive}`}
      style={{ backgroundColor: tint + "55" }}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">{label}</div>
      <div className="font-heading text-2xl font-semibold tracking-tight" style={{ color: text }}>{value}</div>
      <div className="text-[10px] text-[#3F3F3F]/70 mt-0.5">{sub}</div>
    </Tag>
  );
}


/* ------- AdminHistoryPanel (persistent records + filters + export) ------- */
function AdminHistoryPanel({ rangeParams }) {
  const [tab, setTab] = useState("received");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { kind, data, type, id }
  const [lookup, setLookup] = useState({ materials: [], destination_types: [], collectors: [], destinations: [], facilities: [] });
  const [filters, setFilters] = useState({
    material_type: "", collector_id: "", facility_id: "",
    destination_type: "", destination_name: "", admin_status: "",
    from: "", to: "",
  });
  const [exportBusy, setExportBusy] = useState(false);
  const [exportBytes, setExportBytes] = useState(0);
  // Background-job state for admin exports. The sync /facility/export.zip
  // endpoint still exists as a fallback, but the primary path now queues a
  // job and polls `/admin/export-jobs/{id}` every 5s.
  const [exportJob, setExportJob] = useState(null); // { id, status, filename, download_url, file_size, error }
  // Which collection status to include in the compliance package. "all" is
  // the default and keeps every status (Pending, Received, Rejected,
  // Resubmitted, Processed, …). Anything else restricts to that one.
  const [exportStatus, setExportStatus] = useState("all");
  const exportPollRef = useRef(null);

  // Quick-period presets — sets local from/to ISO strings
  function applyPreset(key) {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    if (key === "all") { setFilters((f) => ({ ...f, from: "", to: "" })); return; }
    const to = new Date(now);
    const from = new Date(now);
    if (key === "7d")  from.setDate(now.getDate() - 7);
    if (key === "30d") from.setDate(now.getDate() - 30);
    if (key === "90d") from.setDate(now.getDate() - 90);
    if (key === "ytd") { from.setMonth(0); from.setDate(1); from.setHours(0,0,0,0); }
    setFilters((f) => ({ ...f, from: fmt(from), to: fmt(to) }));
  }

  // Effective range: panel filters take precedence over the page-level range.
  const effectiveRange = useMemo(() => ({
    from: filters.from || rangeParams.from || "",
    to:   filters.to   || rangeParams.to   || "",
  }), [filters.from, filters.to, rangeParams.from, rangeParams.to]);

  useEffect(() => {
    api.get("/lookup/filters").then((r) => setLookup(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setRows(null);
    setLoading(true);
    (async () => {
      try {
        let url = "/admin/collections";
        const params = { ...effectiveRange };
        if (tab === "received") {
          params.status = "Received";
        } else if (tab === "processed") {
          url = "/admin/processings";
        } else if (tab === "shipped") {
          url = "/admin/shipments";
        }
        if (filters.material_type) params.material_type = filters.material_type;
        if (filters.facility_id) params.facility_id = filters.facility_id;
        if (tab === "received" && filters.collector_id) params.collector_id = filters.collector_id;
        if (tab === "shipped") {
          if (filters.destination_type) params.destination_type = filters.destination_type;
          if (filters.destination_name) params.destination_name = filters.destination_name;
          // Map virtual statuses to the raw `admin_status` the backend knows
          // about; the client-side state machine then narrows within "pending".
          if (filters.admin_status === "awaiting_review" || filters.admin_status === "resubmitted") {
            params.admin_status = "pending";
          } else if (filters.admin_status) {
            params.admin_status = filters.admin_status;
          }
        }
        const { data } = await api.get(url, { params });
        let result = data || [];
        if (tab === "shipped" && (filters.admin_status === "awaiting_review" || filters.admin_status === "resubmitted")) {
          result = result.filter((s) => {
            const key = getShipmentReviewState(s).key;
            return key === filters.admin_status;
          });
        }
        setRows(result);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
    // History panel intentionally refetches only on tab/filters/range changes.
  }, [tab, filters, effectiveRange]);

  async function exportFiltered() {
    // Route to the server-formatted CSV so the download always carries the
    // per-material columns (mat_*, input_*, output_*, shipped_*) and the
    // GMT+X timestamp format — matches the evidence ZIP and the admin
    // KPI-tile export exactly.
    const kind = tab === "processed" ? "processings" : tab === "shipped" ? "shipments" : "collections";
    const params = new URLSearchParams({ kind });
    if (effectiveRange.from) params.set("date_from", effectiveRange.from);
    if (effectiveRange.to) params.set("date_to", effectiveRange.to);
    if (filters.material_type) params.set("material_type", filters.material_type);
    if (tab === "received" && filters.collector_id) params.set("collector_id", filters.collector_id);
    if (filters.facility_id) params.set("facility_id", filters.facility_id);
    if (tab === "shipped" && filters.destination_name) params.set("destination_name", filters.destination_name);
    try {
      const res = await api.get(`/admin/export.csv?${params}`, { responseType: "blob" });
      if (!res.data || res.data.size < 30) { toast("Nothing to export with current filters"); return; }
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `process-${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch { toast.error("Export failed"); }
  }

  async function exportCompliancePackage() {
    // Admin flow is now asynchronous: queue a build via /admin/export-jobs,
    // then poll every 5s until the job is 'ready' (surface a Download
    // button) or 'failed' (surface the error). The sync /facility/export.zip
    // endpoint still exists but we only hit it for the facility-user path.
    if (exportBusy) return;
    const isMulti = !filters.facility_id;
    setExportBusy(true);
    setExportJob(null);
    const kickoff = isMulti
      ? "Preparing all-facility evidence package… this can take a minute"
      : "Preparing your file…";
    const toastId = toast.loading(kickoff);
    try {
      const params = {};
      if (filters.facility_id) params.facility_id = filters.facility_id;
      if (effectiveRange.from) params.from = new Date(effectiveRange.from).toISOString();
      if (effectiveRange.to)   params.to   = new Date(effectiveRange.to).toISOString();
      if (exportStatus && exportStatus !== "all") params.collection_status = exportStatus;
      const { data } = await api.post("/admin/export-jobs", null, { params });
      const jobId = data.job_id;
      setExportJob({ id: jobId, status: "building" });

      // Poll every 5s. Clear any previous timer first so repeated clicks
      // never leak overlapping intervals.
      if (exportPollRef.current) clearInterval(exportPollRef.current);
      exportPollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/admin/export-jobs/${jobId}`);
          setExportJob(j);
          if (j.status === "ready") {
            clearInterval(exportPollRef.current);
            exportPollRef.current = null;
            setExportBusy(false);
            toast.success(
              `Your export is ready · ${fmtMB(j.file_size || 0)}`,
              { id: toastId },
            );
          } else if (j.status === "failed") {
            clearInterval(exportPollRef.current);
            exportPollRef.current = null;
            setExportBusy(false);
            toast.error(
              `Export failed: ${j.error || "Unknown error"}`,
              { id: toastId },
            );
          } else {
            toast.loading(
              `${isMulti ? "All facilities · " : ""}Preparing your file…`,
              { id: toastId },
            );
          }
        } catch (err) {
          clearInterval(exportPollRef.current);
          exportPollRef.current = null;
          setExportBusy(false);
          toast.error(
            formatApiError(err.response?.data?.detail) || err.message || "Lost connection to export job",
            { id: toastId },
          );
        }
      }, 5000);
    } catch (e) {
      setExportBusy(false);
      toast.error(
        formatApiError(e.response?.data?.detail) || e.message || "Could not queue export",
        { id: toastId },
      );
    }
  }

  // Clean up the polling timer if the panel unmounts while a job is in flight.
  useEffect(() => () => {
    if (exportPollRef.current) clearInterval(exportPollRef.current);
  }, []);

  function downloadReadyExport() {
    const job = exportJob;
    if (!job || job.status !== "ready") return;
    // `download_url` is relative — anchor to our API base so the signed
    // token survives even when the admin console runs on a different origin
    // (e.g. production vs. preview).
    const base = (api.defaults?.baseURL || "").replace(/\/api$/, "");
    // Prefer the parts list when it exists so multi-month exports pull
    // every bucket. The top-level `download_url` is only populated for
    // legacy single-part jobs.
    const links = (Array.isArray(job.parts) && job.parts.length > 0)
      ? job.parts.map((p) => ({ url: base + p.download_url, name: p.filename || `${p.month}.zip` }))
      : (job.download_url ? [{ url: base + job.download_url, name: job.filename || "compliance_package.zip" }] : []);
    if (links.length === 0) return;
    // Multiple parts — the browser throttles rapid-fire anchor clicks, so
    // stagger each click by a tick to make sure every file actually
    // downloads instead of only the last one surviving the race.
    links.forEach((link, i) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = link.url;
        a.download = link.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }, i * 400);
    });
  }

  function dismissExportJob() {
    if (exportPollRef.current) clearInterval(exportPollRef.current);
    exportPollRef.current = null;
    setExportJob(null);
    setExportBusy(false);
  }

  function resetFilters() {
    setFilters({ material_type: "", collector_id: "", facility_id: "", destination_type: "", destination_name: "", admin_status: "", from: "", to: "" });
  }

  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="admin-history-panel">
      <CardContent className="p-5 md:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Historical records</div>
            <h3 className="font-heading text-lg font-semibold text-[#000000]">All activity (cross-channel)</h3>
            <p className="text-xs text-[#3F3F3F]/70 mt-0.5">
              Pick a time period below (or set From/To) — overrides the page-level range when used.
            </p>
          </div>
        </div>

        {/* Time period presets */}
        <div className="flex flex-wrap items-center gap-2" data-testid="admin-history-periods">
          {[
            { k: "all", l: "All time" },
            { k: "7d",  l: "Last 7 days" },
            { k: "30d", l: "Last 30 days" },
            { k: "90d", l: "Last 90 days" },
            { k: "ytd", l: "Year to date" },
          ].map((p) => {
            const isAll = p.k === "all";
            const active = (isAll && !filters.from && !filters.to);
            return (
              <button
                key={p.k}
                type="button"
                onClick={() => applyPreset(p.k)}
                data-testid={`admin-history-period-${p.k}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                  active
                    ? "bg-[#056839] text-white border-[#056839]"
                    : "bg-white border-[#D2E4D6] text-[#3F3F3F]/70 hover:text-[#056839]"
                }`}
              >
                {p.l}
              </button>
            );
          })}
          <div className="h-6 w-px bg-[#D2E4D6] mx-1" />
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">From</label>
            <input
              type="datetime-local"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              data-testid="admin-history-from"
              className="h-9 text-sm rounded-md border border-input bg-white px-2"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">To</label>
            <input
              type="datetime-local"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              data-testid="admin-history-to"
              className="h-9 text-sm rounded-md border border-input bg-white px-2"
            />
          </div>
        </div>

        {/* Sub-tab pills */}
        <div className="flex items-center gap-2" data-testid="admin-history-subtabs">
          {[
            { v: "received",  l: "Received",  accent: "bg-[#D2E4D6] text-[#056839]" },
            { v: "processed", l: "Processed", accent: "bg-[#E4D5B7] text-[#A0522D]" },
            { v: "shipped",   l: "Shipped",   accent: "bg-[#056839] text-white" },
          ].map(s => (
            <button key={s.v} type="button" onClick={() => setTab(s.v)}
              data-testid={`admin-history-subtab-${s.v}`}
              className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full transition-colors ${
                tab === s.v ? s.accent + " shadow-sm"
                : "text-[#3F3F3F]/60 bg-white border border-[#D2E4D6] hover:text-[#3F3F3F]"
              }`}>
              {s.l}
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-3" data-testid="admin-history-filters">
          <FilterSel label="Material" value={filters.material_type} onChange={(v) => setFilters({ ...filters, material_type: v })}
            options={[["", "All materials"], ...lookup.materials.map(m => [m, m])]} testId="adm-h-material" />
          {tab === "received" && (
            <FilterSel label="Collector" value={filters.collector_id} onChange={(v) => setFilters({ ...filters, collector_id: v })}
              options={[["", "All collectors"], ...lookup.collectors.map(c => [c.id, c.name || c.email])]} testId="adm-h-collector" />
          )}
          <FilterSel label="Facility" value={filters.facility_id} onChange={(v) => setFilters({ ...filters, facility_id: v })}
            options={[["", "All facilities"], ...(lookup.facilities || []).map(f => [f.id, f.name])]} testId="adm-h-facility" />
          {tab === "shipped" && (
            <>
              <FilterSel label="Destination type" value={filters.destination_type} onChange={(v) => setFilters({ ...filters, destination_type: v })}
                options={[["", "All types"], ...lookup.destination_types.map(t => [t, t])]} testId="adm-h-dest-type" />
              <FilterSel label="Treatment partner" value={filters.destination_name} onChange={(v) => setFilters({ ...filters, destination_name: v })}
                options={[["", "All treatment partners"], ...(lookup.destinations || []).map(d => [d.name, d.name])]} testId="adm-h-dest-name" />
              <FilterSel label="Status" value={filters.admin_status} onChange={(v) => setFilters({ ...filters, admin_status: v })}
                options={[
                  ["", "All statuses"],
                  ["awaiting_review", "Awaiting review (new)"],
                  ["resubmitted", "Resubmitted"],
                  ["pending", "All pending"],
                  ["rejected", "Awaiting facility (rejected)"],
                  ["approved", "Approved"],
                ]} testId="adm-h-status" />
            </>
          )}
          <button type="button" onClick={resetFilters} data-testid="admin-history-reset"
            className="text-xs text-[#3F3F3F]/70 underline hover:text-[#056839] h-9 px-2">Reset</button>
          <Button onClick={exportFiltered} data-testid="admin-history-export"
            className="h-9 gfs-bg-primary hover:bg-[#044E2A] text-white">
            <Download className="w-4 h-4 mr-1.5" /> Export filtered records
          </Button>
          <Button
            onClick={exportCompliancePackage}
            data-testid="admin-history-export-zip"
            variant="outline"
            disabled={exportBusy}
            title={
              filters.facility_id
                ? "Queue an evidence ZIP for the selected facility and date range."
                : "No facility selected — this will pull EVERY active facility and may be large."
            }
            className="h-9 border-[#056839] text-[#056839] hover:bg-[#D2E4D6] disabled:opacity-60"
          >
            {exportBusy ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Preparing your file…
              </>
            ) : (
              <>
                <Package className="w-4 h-4 mr-1.5" />
                Compliance package{filters.facility_id ? "" : " (all)"}
              </>
            )}
          </Button>

          {/* Export status filter — restricts which collection status(es)
              land in the ZIP. "All" is the default and keeps every status. */}
          <select
            value={exportStatus}
            onChange={(e) => setExportStatus(e.target.value)}
            disabled={exportBusy}
            data-testid="admin-export-status"
            title="Include which collection status in the compliance package"
            className="h-9 px-2 rounded-md border border-[#056839]/50 bg-white text-xs text-[#3F3F3F] disabled:opacity-60"
          >
            <option value="all">All statuses</option>
            <option value="Pending">Pending only</option>
            <option value="Received">Received only</option>
            <option value="Rejected">Rejected only</option>
            <option value="Resubmitted">Resubmitted only</option>
            <option value="Processed">Processed only</option>
          </select>

          {exportJob?.status === "ready" && (
            <div
              data-testid="export-ready-banner"
              className="flex flex-col gap-2 px-3 py-2 rounded-md bg-[#D2E4D6]/80 border border-[#056839]/30 text-xs text-[#056839] max-w-full"
            >
              <div className="flex items-center gap-2 flex-wrap">
                {(exportJob.parts || []).length === 0 ? (
                  <>
                    <span className="font-medium">No records in the selected range</span>
                    <span className="opacity-70">· nothing to download</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">
                      {exportJob.parts.length > 1
                        ? `Your export is ready · ${exportJob.parts.length} archives`
                        : "Your export is ready"}
                    </span>
                    <span className="opacity-70">· {fmtMB(exportJob.total_file_size || exportJob.file_size || 0)}</span>
                    <Button
                      size="sm"
                      data-testid="export-ready-download"
                      onClick={downloadReadyExport}
                      className="h-7 bg-[#056839] hover:bg-[#044e2b] text-white"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      {exportJob.parts.length > 1 ? "Download all" : "Download"}
                    </Button>
                  </>
                )}
                <button
                  type="button"
                  onClick={dismissExportJob}
                  className="text-[#056839]/60 hover:text-[#056839] text-xs underline"
                >
                  dismiss
                </button>
              </div>
              {/* Per-part links: when the job split into multiple
                  archives, surface every one so the admin can retrigger
                  any specific slice if a browser pop-up blocker or a
                  stalled download killed one of the auto-fired links. */}
              {Array.isArray(exportJob.parts) && exportJob.parts.length > 1 && (
                <div
                  data-testid="export-ready-parts"
                  className="flex flex-col gap-1 pl-2 border-l border-[#056839]/30 mt-1 max-h-56 overflow-auto"
                >
                  {exportJob.parts.map((p) => {
                    const base = (api.defaults?.baseURL || "").replace(/\/api$/, "");
                    const href = base + p.download_url;
                    return (
                      <a
                        key={p.part_id || `${p.facility_id}-${p.month}`}
                        href={href}
                        download={p.filename || `${p.month}.zip`}
                        className="flex items-center gap-2 text-[11px] text-[#056839] hover:text-[#044e2b] hover:underline"
                        data-testid={`export-part-${p.part_id || p.month}`}
                      >
                        <Download className="w-3 h-3 shrink-0" />
                        <span className="font-mono">{p.month}</span>
                        <span className="opacity-70">·</span>
                        <span className="truncate">{p.facility_name || p.facility_id || "—"}</span>
                        <span className="opacity-60 ml-auto whitespace-nowrap">{fmtMB(p.file_size || 0)}</span>
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {exportJob?.status === "failed" && (
            <div
              data-testid="export-failed-banner"
              className="flex items-center gap-2 px-3 h-9 rounded-md bg-red-50 border border-red-200 text-xs text-red-700"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Export failed: {exportJob.error || "Unknown error"}</span>
              <button
                type="button"
                onClick={dismissExportJob}
                className="text-red-700/60 hover:text-red-700 text-xs underline"
              >
                dismiss
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-[#D2E4D6]/60" data-testid={`admin-history-table-${tab}`}>
          {loading ? (
            <div className="text-sm text-[#3F3F3F]/60 py-8 text-center">Loading…</div>
          ) : !rows || rows.length === 0 ? (
            <div className="text-sm text-[#3F3F3F]/60 py-8 text-center">No records match these filters.</div>
          ) : tab === "received" ? (
            <table className="w-full text-sm">
              <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Collector</th>
                  <th className="px-3 py-2 text-left">Material</th>
                  <th className="px-3 py-2 text-right">Recv. kg</th>
                  <th className="px-3 py-2 text-right">Paid</th>
                  <th className="px-3 py-2 text-left">Facility</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setDetail({ kind: "received", data: r, type: "collection", id: r.id })}
                    data-testid={`adm-h-row-${r.id}`}
                    className="border-t border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer">
                    <td className="px-3 py-2">{formatDateTime(r.timestamp)}</td>
                    <td className="px-3 py-2">{r.collector_name ? `${r.collector_name}${idSuffix(r.collector_id)}` : "—"}</td>
                    <td className="px-3 py-2">{r.material_type || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(r.received_weight_kg || 0).toFixed(1)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.transaction_amount != null ? `${r.transaction_amount} ${r.transaction_currency || ""}` : "—"}</td>
                    <td className="px-3 py-2">{r.facility_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tab === "processed" ? (
            <table className="w-full text-sm">
              <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Facility</th>
                  <th className="px-3 py-2 text-left">Types</th>
                  <th className="px-3 py-2 text-right">Input kg</th>
                  <th className="px-3 py-2 text-left">Outputs</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setDetail({ kind: "processed", data: r, type: "processing", id: r.id })}
                    data-testid={`adm-h-row-${r.id}`}
                    className="border-t border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer">
                    <td className="px-3 py-2">{formatDateTime(r.processing_date)}</td>
                    <td className="px-3 py-2">{r.facility_name || "—"}</td>
                    <td className="px-3 py-2">{(r.processing_types || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(r.total_input_weight_kg || 0).toFixed(1)}</td>
                    <td className="px-3 py-2 text-xs">{(r.outputs || []).map(o => `${o.material_type}:${Number(o.weight_kg||0).toFixed(0)}kg`).join(" · ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#D2E4D6]/40 text-[10px] uppercase tracking-wider text-[#3F3F3F]">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Facility</th>
                  <th className="px-3 py-2 text-left">Destinations</th>
                  <th className="px-3 py-2 text-right">Shipped kg</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.flatMap((s) => (s.destinations || [{}]).map((d, i) => (
                  <tr key={`${s.id}-${i}`} onClick={() => setDetail({ kind: "shipped", data: { shipment: s, destination: d }, type: "shipment", id: s.id, admin_status: s.admin_status })}
                    data-testid={`adm-h-row-${s.id}-${i}`}
                    className="border-t border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer">
                    <td className="px-3 py-2">{formatDateTime(s.created_at)}</td>
                    <td className="px-3 py-2">{s.facility_name || "—"}</td>
                    <td className="px-3 py-2">{d.destination_name || s.destination_name || "—"} {d.destination_type ? <span className="text-[10px] uppercase tracking-wider text-[#69A488] ml-1">{d.destination_type}</span> : null}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(d.weight_kg || s.total_shipped_kg || 0).toFixed(1)}</td>
                    <td className="px-3 py-2">
                      {(() => {
                        const st = getShipmentReviewState(s);
                        const Icon = st.Icon;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${st.pillClass}`}
                            title={st.label}
                          >
                            {Icon && <Icon className="w-3 h-3" />}
                            {st.shortLabel || st.label}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          )}
        </div>
        <div className="text-[11px] text-[#3F3F3F]/60" data-testid="admin-history-count">
          {rows ? `${rows.length} records` : ""}
        </div>
      </CardContent>
      <HistoryDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </Card>
  );
}

function FilterSel({ label, value, onChange, options, testId }) {
  return (
    <div className="min-w-[140px]">
      <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}


/* ------- StatCard (clickable) ------- */
function StatCard({ label, value, sub, icon, tint, color, onClick, testId }) {
  const useDark = tint === "#056839";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`group text-left rounded-xl p-5 md:p-6 border shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 ${
        useDark ? "" : "bg-white border-[#D2E4D6]/60"
      }`}
      style={useDark ? { background: "#056839", borderColor: "#056839" } : {}}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-xs uppercase tracking-[0.15em] font-medium ${useDark ? "text-[#D2E4D6]" : "text-[#69A488]"}`}>
            {label}
          </div>
          <div
            className="font-heading text-3xl md:text-4xl font-semibold tracking-tighter mt-2"
            style={{ color: useDark ? "#FFFFFF" : color || "#056839" }}
          >
            {value}
          </div>
          <div className={`text-xs mt-1 ${useDark ? "text-white/70" : "text-[#3F3F3F]/70"}`}>
            {sub}
          </div>
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{
            background: useDark ? "rgba(255,255,255,0.15)" : tint || "#D2E4D6",
            color: useDark ? "#FFFFFF" : color || "#056839",
          }}
        >
          {icon}
        </div>
      </div>
      <div className={`mt-3 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider transition-opacity ${useDark ? "text-white/80" : "text-[#056839]/80"} opacity-0 group-hover:opacity-100`}>
        View history <ArrowUpRight className="w-3 h-3" />
      </div>
    </button>
  );
}

function EmptyChart() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-[#3F3F3F]/50 text-sm">
      <AlertTriangle className="w-6 h-6 mb-2 opacity-40" />
      No data in this period.
    </div>
  );
}

/* ------- Detail dialog for a KPI ------- */
function DetailDialog({ kind: initialKind, onClose, rangeParams, initialFilters = {}, title, allowKindSwitch = false }) {
  const [kind, setKind] = useState(initialKind);
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null); // { kind, data }
  const [lookup, setLookup] = useState({ materials: [], destination_types: [], collectors: [], destinations: [], facilities: [] });
  const [filters, setFilters] = useState({
    material_type: "",
    collector_id: "",
    facility_id: "",
    destination_type: "",
    destination_name: "",
    admin_status: "",
    channel_id: "",
    status: "",
  });

  // Sync internal kind whenever the dialog is (re-)opened from outside
  useEffect(() => {
    if (initialKind) setKind(initialKind);
  }, [initialKind]);

  useEffect(() => {
    if (!initialKind) return;
    api.get("/lookup/filters").then((r) => setLookup(r.data)).catch(() => {});
    // Reset filters when dialog opens with a new initial kind / channel
    setFilters({
      material_type: "",
      collector_id: "",
      facility_id: "",
      destination_type: "",
      destination_name: "",
      admin_status: "",
      channel_id: "",
      status: "",
      ...initialFilters,
    });
    // initialFilters is a parent-defined object; only its primitive keys
    // matter, so we intentionally depend on those rather than the whole object.
  }, [initialKind, initialFilters.channel_id, initialFilters.admin_status, initialFilters.status]);

  useEffect(() => {
    if (!kind) return;
    setRows(null);
    (async () => {
      try {
        let url = "/admin/collections";
        const params = { ...rangeParams };
        if (kind === "collected") {
          if (filters.status) params.status = filters.status;
        } else if (kind === "received") {
          params.status = "Received";
        } else if (kind === "processed") {
          url = "/admin/processings";
        } else if (kind === "shipped") {
          url = "/admin/shipments";
        }
        if (filters.material_type) params.material_type = filters.material_type;
        if (filters.facility_id) params.facility_id = filters.facility_id;
        if ((kind === "collected" || kind === "received") && filters.collector_id) {
          params.collector_id = filters.collector_id;
        }
        if (kind === "shipped") {
          if (filters.destination_type) params.destination_type = filters.destination_type;
          if (filters.destination_name) params.destination_name = filters.destination_name;
          if (filters.admin_status === "awaiting_review" || filters.admin_status === "resubmitted") {
            params.admin_status = "pending";
          } else if (filters.admin_status) {
            params.admin_status = filters.admin_status;
          }
        }
        const { data } = await api.get(url, { params });
        let result = data;
        if (kind === "shipped" && (filters.admin_status === "awaiting_review" || filters.admin_status === "resubmitted")) {
          result = (data || []).filter((s) => getShipmentReviewState(s).key === filters.admin_status);
        }
        setRows(result);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
    // Detail dialog refetches when the kind/filters/range change.
  }, [kind, filters, rangeParams]);

  if (!initialKind) return null;

  const titleMap = {
    collected: "All collections",
    received: "Received deliveries",
    processed: "Processing runs",
    shipped: "Shipments to destinations",
  };

  return (
    <Dialog open={!!kind} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-hidden p-0 flex flex-col"
        data-testid={`detail-dialog-${kind}`}
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#D2E4D6] shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <DialogTitle className="font-heading text-2xl">{title || titleMap[kind]}</DialogTitle>
              <div className="text-xs text-[#3F3F3F]/60 mt-1">
                {rangeParams.from || rangeParams.to
                  ? `${rangeParams.from ? formatDateTime(rangeParams.from) : "All time"} → ${rangeParams.to ? formatDateTime(rangeParams.to) : "now"}`
                  : "All time"}
              </div>
            </div>            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  const recordType = kind === "processed" ? "processing" : kind === "shipped" ? "shipments" : "collections";
                  const exportKind = recordType === "processing" ? "processings" : recordType;
                  const params = new URLSearchParams({ kind: exportKind });
                  if (rangeParams.from) params.set("date_from", rangeParams.from);
                  if (rangeParams.to) params.set("date_to", rangeParams.to);
                  if (filters.material_type) params.set("material_type", filters.material_type);
                  if (filters.collector_id && (exportKind === "collections")) params.set("collector_id", filters.collector_id);
                  if (filters.facility_id) params.set("facility_id", filters.facility_id);
                  if (exportKind === "shipments" && filters.destination_name) params.set("destination_name", filters.destination_name);
                  try {
                    const res = await api.get(`/admin/export.csv?${params}`, { responseType: "blob" });
                    if (!res.data || res.data.size < 30) { toast("No records to export"); return; }
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `process-${exportKind}-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success("Exported CSV");
                  } catch { toast.error("Export failed"); }
                }}
                data-testid="detail-export-btn"
                className="gfs-bg-primary hover:bg-[#044E2A] text-white"
              >
                <Download className="w-4 h-4 mr-1.5" /> Export CSV (filtered)
              </Button>
            </div>
          </div>
        </DialogHeader>
        {allowKindSwitch && (
          <div className="px-6 pt-3 flex items-center gap-1 border-b border-[#D2E4D6] shrink-0" data-testid="detail-kind-tabs">
            {[
              { v: "collected", l: "Collections" },
              { v: "processed", l: "Processed" },
              { v: "shipped",   l: "Shipped" },
            ].map((t) => (
              <button
                key={t.v}
                type="button"
                onClick={() => setKind(t.v)}
                data-testid={`detail-kind-tab-${t.v}`}
                className={`px-3 py-2 text-sm font-medium relative ${
                  kind === t.v ? "text-[#056839]" : "text-[#3F3F3F]/60 hover:text-[#3F3F3F]"
                }`}
              >
                {t.l}
                {kind === t.v && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-[#056839]" />}
              </button>
            ))}
          </div>
        )}
        <div className="px-6 pt-3 pb-3 border-b border-[#D2E4D6]/60 shrink-0" data-testid="detail-filters">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="min-w-[140px]">
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Material</label>
              <select
                value={filters.material_type}
                onChange={(e) => setFilters({ ...filters, material_type: e.target.value })}
                data-testid="detail-filter-material"
                className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
              >
                <option value="">All</option>
                {lookup.materials.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="min-w-[160px]">
              <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Facility</label>
              <select
                value={filters.facility_id}
                onChange={(e) => setFilters({ ...filters, facility_id: e.target.value })}
                data-testid="detail-filter-facility"
                className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
              >
                <option value="">All</option>
                {lookup.facilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            {(kind === "collected" || kind === "received") && (
              <div className="min-w-[160px]">
                <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Collector</label>
                <select
                  value={filters.collector_id}
                  onChange={(e) => setFilters({ ...filters, collector_id: e.target.value })}
                  data-testid="detail-filter-collector"
                  className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
                >
                  <option value="">All</option>
                  {lookup.collectors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {kind === "shipped" && (
              <>
                <div className="min-w-[140px]">
                  <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Destination type</label>
                  <select
                    value={filters.destination_type}
                    onChange={(e) => setFilters({ ...filters, destination_type: e.target.value })}
                    data-testid="detail-filter-dest-type"
                    className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
                  >
                    <option value="">All</option>
                    {lookup.destination_types.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="min-w-[200px]">
                  <label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Treatment partner</label>
                  <select
                    value={filters.destination_name}
                    onChange={(e) => setFilters({ ...filters, destination_name: e.target.value })}
                    data-testid="detail-filter-partner"
                    className="h-9 mt-1 w-full text-sm rounded-md border border-input bg-white px-3"
                  >
                    <option value="">All treatment partners</option>
                    {lookup.destinations.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
                  </select>
                </div>
              </>
            )}
            <button
              type="button"
              onClick={() => setFilters({ material_type: "", collector_id: "", facility_id: "", destination_type: "", destination_name: "", admin_status: "", channel_id: filters.channel_id, status: filters.status })}
              data-testid="detail-filters-reset"
              className="text-xs text-[#3F3F3F]/70 underline hover:text-[#056839] h-9 px-2"
            >
              Reset filters
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto px-6 pb-6" data-testid="detail-content">
          {(() => {
            // Apply client-side channel filter on top of server-fetched rows
            const visibleRows = rows == null ? null
              : (!filters.channel_id ? rows
                : rows.filter(r => r.channel_id === filters.channel_id));
            if (visibleRows === null) return <div className="p-8 text-center text-sm text-[#3F3F3F]/60">Loading…</div>;
            if (visibleRows.length === 0) return (
              <div className="p-12 text-center text-sm text-[#3F3F3F]/60">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No records in this period.
              </div>
            );
            if (kind === "processed") return <ProcessingsList rows={visibleRows} onRowClick={(p) => setSelected({ kind: "processed", data: p })} />;
            if (kind === "shipped")   return <ShipmentsList rows={visibleRows} onRowClick={(d) => setSelected({ kind: "shipped", data: d, type: "shipment", id: d.shipment?.id, admin_status: d.shipment?.admin_status })} />;
            return <CollectionsList rows={visibleRows} onRowClick={(c) => setSelected({ kind: "received", data: c, type: "collection", id: c.id })} />;
          })()}
        </div>
        <HistoryDetailDialog detail={selected} onClose={() => setSelected(null)} />
      </DialogContent>
    </Dialog>
  );
}

function CollectionsList({ rows, onRowClick }) {
  return (
    <table className="w-full text-sm" data-testid="collections-detail-table">
      <thead className="sticky top-0 bg-[#D2E4D6]/80 backdrop-blur z-10">
        <tr className="text-left text-[11px] uppercase tracking-wider text-[#3F3F3F]">
          <th className="px-3 py-2">When</th>
          <th className="px-3 py-2">Source · address</th>
          <th className="px-3 py-2">Photo</th>
          <th className="px-3 py-2">Material</th>
          <th className="px-3 py-2 text-right">Est / Recv (kg)</th>
          <th className="px-3 py-2">Paid</th>
          <th className="px-3 py-2">Collector</th>
          <th className="px-3 py-2">Facility</th>
          <th className="px-3 py-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr
            key={c.id}
            onClick={() => onRowClick && onRowClick(c)}
            data-testid={`admin-collection-row-${c.id}`}
            className="border-t border-[#D2E4D6]/60 align-top cursor-pointer hover:bg-[#F9F8F6]"
          >
            <td className="px-3 py-2 text-[#3F3F3F]">
              <div className="font-medium text-[#000000]">
                {formatDateTime(c.pickup_at || c.timestamp)}
              </div>
              {c.date_received && (
                <div className="text-[10px] text-[#3F3F3F]/60">
                  Received {formatDate(c.date_received)}
                </div>
              )}
              <RecordId id={c.id} />
            </td>
            <td className="px-3 py-2 text-[#3F3F3F] max-w-[240px]">
              <div className="font-medium text-[#000000]">{c.source_location}</div>
              {c.address && (
                <div className="text-[11px] text-[#3F3F3F]/70 mt-0.5 line-clamp-2">{c.address}</div>
              )}
              {c.reference_number && (
                <div className="text-[11px] text-[#056839] font-medium mt-0.5" data-testid={`admin-collection-ref-${c.id}`}>
                  Ref: {c.reference_number}
                </div>
              )}
            </td>
            <td className="px-3 py-2">
              <PhotoThumbs collection={c} testIdPrefix={`admin-photo-thumb-${c.id}`} />
            </td>
            <td className="px-3 py-2 font-medium">{c.material_type}</td>
            <td className="px-3 py-2 text-right font-mono">
              {c.estimated_weight_kg || "—"} / <span className="text-[#056839] font-semibold">{c.received_weight_kg || "—"}</span>
            </td>
            <td className="px-3 py-2 whitespace-nowrap">
              {c.transaction_amount != null ? (
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[#000000]">
                    {Number(c.transaction_amount).toFixed(2)} {c.transaction_currency || ""}
                  </span>
                  {c.payment_proof_path && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURI(c.payment_proof_path)}`}
                      target="_blank"
                      rel="noreferrer"
                    download
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`admin-payment-proof-${c.id}`}
                      className="text-[10px] uppercase tracking-wider text-[#056839] underline"
                    >
                      proof
                    </a>
                  )}
                </div>
              ) : (
                <span className="text-[#3F3F3F]/40 text-xs">—</span>
              )}
            </td>
            <td className="px-3 py-2">{c.collector_name ? `${c.collector_name}${idSuffix(c.collector_id)}` : "—"}</td>
            <td className="px-3 py-2">{c.destination_facility_name ? `${c.destination_facility_name}${idSuffix(c.destination_facility_id)}` : "—"}</td>
            <td className="px-3 py-2">
              <StatusPill status={c.status} reason={c.rejection_reason} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProcessingsList({ rows, onRowClick }) {
  return (
    <div className="space-y-3" data-testid="processings-detail-list">
      {rows.map((p) => (
        <Card
          key={p.id}
          onClick={() => onRowClick && onRowClick(p)}
          data-testid={`admin-processing-row-${p.id}`}
          className="border-[#D2E4D6]/60 cursor-pointer hover:shadow-md transition-shadow"
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#69A488]">
                  {p.facility_name || "Unknown facility"}
                </div>
                <div className="font-heading text-base font-semibold text-[#000000]">
                  {(p.processing_types || [p.processing_type]).filter(Boolean).join(" + ")}
                </div>
                <div className="text-[11px] text-[#3F3F3F]/60 mt-0.5">
                  {formatDateTime(p.processing_date || p.created_at)}
                </div>
                <RecordId id={p.id} />
              </div>
              <div className="text-right">
                <div className="text-xs text-[#3F3F3F]/70">Total input</div>
                <div className="font-mono font-semibold text-[#056839]">
                  {(p.total_input_weight_kg || 0).toFixed(1)} kg
                </div>
                <div className="text-[10px] text-[#3F3F3F]/60">
                  Residual {(p.residual_weight_kg || 0).toFixed(1)} kg
                </div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {(p.outputs || []).map((o) => (
                <div
                  key={o.id}
                  className={`text-xs rounded-lg px-3 py-2 flex items-center justify-between ${
                    o.shipped ? "bg-[#D2E4D6]" : "bg-[#E4D5B7]/60"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Package className="w-3.5 h-3.5" />
                    <span className="font-medium">
                      {o.material_type} · {o.form}
                    </span>
                  </div>
                  <div className="font-mono">
                    {o.weight_kg} kg
                    <span className={`ml-2 text-[10px] uppercase tracking-wider ${o.shipped ? "text-[#056839]" : "text-[#A0522D]"}`}>
                      {o.shipped ? "Shipped" : "In stock"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ShipmentsList({ rows, onRowClick }) {
  return (
    <div className="space-y-3" data-testid="shipments-detail-list">
      {rows.map((s) => {
        const dests = s.destinations || [];
        return (
        <Card key={s.id} className="border-[#D2E4D6]/60">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-wider text-[#69A488]">
                  {s.facility_name || "Unknown facility"}
                </div>
                <div className="font-heading text-base font-semibold text-[#000000]">
                  Shipment · {dests.length} destination{dests.length > 1 ? "s" : ""}
                </div>
                <div className="text-[11px] text-[#3F3F3F]/60 mt-0.5">
                  {formatDateTime(s.created_at)}
                </div>
                <RecordId id={s.id} />
              </div>
              <div className="text-right">
                <div className="text-xs text-[#3F3F3F]/70">Shipped</div>
                <div className="font-mono font-semibold text-[#056839]">
                  {(s.total_shipped_kg || 0).toFixed(1)} kg
                </div>
                {(() => {
                  const st = getShipmentReviewState(s);
                  const Icon = st.Icon;
                  return (
                    <span
                      className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${st.pillClass}`}
                      title={st.label}
                      data-testid={`shipment-card-state-${s.id}`}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      {st.shortLabel || st.label}
                    </span>
                  );
                })()}
              </div>
            </div>
            <div className="mt-3 space-y-2">
              {dests.map((d, i) => (
                <div
                  key={d.id || i}
                  onClick={() => onRowClick && onRowClick({ shipment: s, destination: d })}
                  data-testid={`admin-shipment-row-${s.id}-${i}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-2 text-xs items-center bg-[#F9F8F6] border border-[#D2E4D6]/60 rounded-lg px-3 py-2 cursor-pointer hover:bg-[#D2E4D6]/30 transition-colors"
                >
                  <div className="md:col-span-2 flex items-center gap-1.5">
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md"
                      style={{ background: (OUTCOME_COLORS[d.destination_type] || "#69A488") + "22", color: OUTCOME_COLORS[d.destination_type] || "#69A488" }}
                    >
                      {d.destination_type}
                    </span>
                    {s.shipment_source === "surplus_sale" && (
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md bg-[#A0522D]/15 text-[#A0522D]"
                        title="Surplus sale — no linked processing batch"
                        data-testid={`admin-shipment-surplus-${s.id}-${i}`}
                      >
                        Surplus
                      </span>
                    )}
                  </div>
                  <div className="md:col-span-4 font-medium text-[#000000] truncate">
                    {d.destination_name}
                  </div>
                  <div className="md:col-span-2 font-medium">{d.material_type}</div>
                  <div className="md:col-span-2 font-mono text-[#056839]">{d.weight_kg} kg</div>
                  <div className="md:col-span-2 text-[10px] text-[#3F3F3F]/70">
                    {formatDate(d.transaction_at)}
                    {d.invoice_path && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(d.invoice_path)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="ml-2 text-[#056839] underline"
                      >
                        invoice
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}

function PhotoThumb({ path, testId }) {
  if (!path) return <span className="text-xs text-[#3F3F3F]/40">—</span>;
  const src = `${process.env.REACT_APP_BACKEND_URL}/api/files/${encodeURIComponent(path)}`;
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
      className="block w-12 h-12 rounded-md overflow-hidden border border-[#D2E4D6] hover:scale-110 transition-transform"
    >
      <img
        src={src}
        alt="collection"
        className="w-full h-full object-cover"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
    </a>
  );
}

function PhotoThumbs({ collection, testIdPrefix }) {
  const photos = Array.isArray(collection?.photos) ? collection.photos : [];
  const paths = photos
    .map((p) => (p && typeof p === "object" ? (p.path || p.storage_path) : p))
    .filter(Boolean);
  if (paths.length === 0 && collection?.photo_path) paths.push(collection.photo_path);
  if (paths.length === 0) return <span className="text-xs text-[#3F3F3F]/40">—</span>;
  const visible = paths.slice(0, 3);
  const extra = paths.length - visible.length;
  return (
    <div className="flex items-center gap-1" data-testid={testIdPrefix}>
      {visible.map((p, i) => (
        <PhotoThumb key={typeof p === "string" ? p : (p?.path || p?.storage_path || `photo-${i}`)} path={p} testId={`${testIdPrefix}-${i}`} />
      ))}
      {extra > 0 && (
        <span
          className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-[#D2E4D6] text-[#056839] text-xs font-semibold"
          data-testid={`${testIdPrefix}-more`}
        >
          +{extra}
        </span>
      )}
    </div>
  );
}

function StatusPill({ status, reason }) {
  const map = {
    Pending: "bg-[#E4D5B7] text-[#A0522D]",
    Received: "bg-[#D2E4D6] text-[#056839]",
    Rejected: "bg-[#A0522D]/15 text-[#A0522D]",
  };
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${map[status]}`}
      title={reason || ""}
    >
      {status}
    </span>
  );
}


/* ------------- Account requests card ------------- */
function AddUserCard({ onCreated, pending = [], allUsers = [] }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const blank = {
    name: "", email: "", role: "collector",
    channel_id: "everlantern",
    phone: "", address: "", business_registration: "",
    company_name: "",
    facility_name: "", registration_number: "",
    materials_processed: [],
    employees_count: "",
  };
  const [f, setF] = useState(blank);

  const CHANNELS_LIST = [
    ["everlantern", "Everlantern"],
    ["hong_zheng",  "Hong Zheng"],
    ["newgates",    "Newgates"],
    ["hl",          "H&L"],
  ];
  const MATERIALS = ["PET", "HDPE", "HDPE(N)", "PP", "PP(N)", "PE(N)", "PE(M)", "UBC", "Mixed Plastics"];
  const ROLE_OPTIONS = [
    ["collector", "Collector"],
    ["facility",  "Facility"],
    ["viewer",    "Viewer"],
  ];

  function toggleMaterial(m) {
    setF((p) => {
      const has = (p.materials_processed || []).includes(m);
      return { ...p, materials_processed: has
        ? p.materials_processed.filter((x) => x !== m)
        : [...(p.materials_processed || []), m] };
    });
  }

  async function submit(e) {
    e?.preventDefault();
    setErr("");
    if (!f.name.trim() || !f.email.trim()) { setErr("Name and email are required"); return; }
    if ((f.role === "collector" || f.role === "facility") && !f.channel_id) {
      setErr("Channel is required for Collector and Facility roles"); return;
    }
    if (f.role === "facility" && !f.facility_name.trim()) {
      setErr("Facility name is required for Facility role"); return;
    }
    setBusy(true);
    try {
      const body = {
        name: f.name.trim(),
        email: f.email.trim(),
        role: f.role,
        channel_id: (f.role === "viewer") ? null : f.channel_id,
        phone: f.phone.trim() || undefined,
        address: f.address.trim() || undefined,
        business_registration: f.business_registration.trim() || undefined,
      };
      if (f.role === "collector") body.company_name = f.company_name.trim() || undefined;
      if (f.role === "facility") {
        body.facility_name = f.facility_name.trim();
        body.registration_number = f.registration_number.trim() || undefined;
        body.materials_processed = f.materials_processed.length ? f.materials_processed : undefined;
        body.employees_count = f.employees_count ? parseInt(f.employees_count, 10) : undefined;
      }
      await api.post("/users", body);
      toast.success(`Account created. Welcome email sent to ${body.email}`);
      setF(blank);
      setOpen(false);
      onCreated?.();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl" data-testid="add-user-card">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">User management</div>
            <h3 className="font-heading text-lg font-semibold text-[#000000]">Provision a new account</h3>
            <p className="text-xs text-[#3F3F3F]/70 mt-0.5">
              Admins create accounts directly — the user receives an email with a temporary password and must set a new one on first login.
            </p>
          </div>
          <Button
            onClick={() => { setF(blank); setErr(""); setOpen(true); }}
            data-testid="add-user-open-btn"
            className="h-10 gfs-bg-primary hover:bg-[#044E2A] text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Add user
          </Button>
        </div>

        {/* Existing users summary (read-only) */}
        <div className="mt-5 text-xs text-[#3F3F3F]/70">
          <div>Pending: <strong>{pending.length}</strong> · Total users: <strong>{allUsers.length}</strong></div>
        </div>
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add user</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Full name *</Label>
                <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                       data-testid="add-user-name" className="mt-1 h-10" autoFocus />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Email *</Label>
                <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                       data-testid="add-user-email" className="mt-1 h-10" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Role *</Label>
                <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}
                        data-testid="add-user-role" className="mt-1 h-10 w-full text-sm rounded-md border border-input bg-white px-3">
                  {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              {(f.role === "collector" || f.role === "facility") && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Channel *</Label>
                  <select value={f.channel_id} onChange={(e) => setF({ ...f, channel_id: e.target.value })}
                          data-testid="add-user-channel" className="mt-1 h-10 w-full text-sm rounded-md border border-input bg-white px-3">
                    {CHANNELS_LIST.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Phone</Label>
                <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                       data-testid="add-user-phone" className="mt-1 h-10" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Address</Label>
                <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}
                       data-testid="add-user-address" className="mt-1 h-10" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Business registration #</Label>
                <Input value={f.business_registration} onChange={(e) => setF({ ...f, business_registration: e.target.value })}
                       data-testid="add-user-business-reg" className="mt-1 h-10" />
              </div>
              {f.role === "collector" && (
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Company name</Label>
                  <Input value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })}
                         data-testid="add-user-company" className="mt-1 h-10" />
                </div>
              )}
              {f.role === "facility" && (
                <>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Facility name *</Label>
                    <Input value={f.facility_name} onChange={(e) => setF({ ...f, facility_name: e.target.value })}
                           data-testid="add-user-facility-name" className="mt-1 h-10" />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Registration number</Label>
                    <Input value={f.registration_number} onChange={(e) => setF({ ...f, registration_number: e.target.value })}
                           data-testid="add-user-reg-number" className="mt-1 h-10" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Materials processed</Label>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="add-user-materials">
                      {MATERIALS.map((m) => {
                        const on = (f.materials_processed || []).includes(m);
                        return (
                          <button key={m} type="button" onClick={() => toggleMaterial(m)}
                                  className={`px-3 py-1.5 text-xs rounded-full border ${on ? "bg-[#056839] text-white border-[#056839]" : "bg-white text-[#3F3F3F] border-[#D2E4D6]"}`}>
                            {m}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">Employees count</Label>
                    <Input type="number" min="0" value={f.employees_count}
                           onChange={(e) => setF({ ...f, employees_count: e.target.value })}
                           data-testid="add-user-employees" className="mt-1 h-10" />
                  </div>
                </>
              )}
            </div>

            {err && (
              <div className="text-sm text-[#A0522D] bg-[#A0522D]/10 border border-[#A0522D]/30 rounded-lg p-3" data-testid="add-user-error">
                {err}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="add-user-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={busy} data-testid="add-user-submit"
                      className="gfs-bg-primary hover:bg-[#044E2A] text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


/* ------------- Users table (Users tab) ------------- */
const CHANNEL_LABELS = {
  everlantern: "Everlantern",
  hong_zheng: "Hong Zheng",
  newgates: "Newgates",
  hl: "H&L",
};

const ROLE_PILL = {
  collector: "bg-[#D2E4D6] text-[#056839]",
  facility:  "bg-[#DBE7F1] text-[#1E4E79]",
  viewer:    "bg-[#E4E4E4] text-[#3F3F3F]",
  admin:     "bg-[#3F3F3F] text-white",
  superadmin: "bg-[#000000] text-white",
};

const STATUS_PILL = {
  Active:    "bg-[#D2E4D6] text-[#056839]",
  Pending:   "bg-[#E4D5B7] text-[#A0522D]",
  Suspended: "bg-[#A0522D]/15 text-[#A0522D]",
  Rejected:  "bg-[#A0522D]/15 text-[#A0522D]",
  Inactive:  "bg-[#E4E4E4] text-[#3F3F3F]",
};

function formatCreatedAt(iso) {
  return formatDate(iso);
}

function UsersTable({ users, onChanged }) {
  const [query, setQuery] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const sorted = useMemo(() => {
    return [...(users || [])].sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return tb - ta;
    });
  }, [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.role || "").toLowerCase().includes(q)
    );
  }, [sorted, query]);

  async function resendInvite(user) {
    setResendingId(user.id);
    try {
      await api.post(`/admin/users/${user.id}/resend-invite`);
      toast.success(`Invite resent to ${user.email}`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Could not resend invite");
    } finally {
      setResendingId(null);
    }
  }

  return (
    <Card className="border-[#D2E4D6]/80 rounded-xl mt-6" data-testid="users-table-card">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#69A488]">Directory</div>
            <h3 className="font-heading text-lg font-semibold text-[#000000]" data-testid="users-table-count">
              {filtered.length} user{filtered.length === 1 ? "" : "s"}
              {query && filtered.length !== sorted.length && (
                <span className="text-sm font-normal text-[#3F3F3F]/60"> (of {sorted.length})</span>
              )}
            </h3>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#69A488]" />
            <Input
              type="search"
              placeholder="Search by name, email or role"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="users-table-search"
              className="h-10 pl-9 w-72"
            />
          </div>
        </div>

        {sorted.length === 0 ? (
          <div
            className="text-center py-10 text-sm text-[#3F3F3F]/60"
            data-testid="users-table-empty"
          >
            No users yet. Use <strong>Add user</strong> to create the first account.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#3F3F3F]/70 border-b border-[#D2E4D6]">
                  <th className="px-3 py-2 font-medium">Name</th>
                  <th className="px-3 py-2 font-medium">Email</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">Channel</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr
                    key={u.id}
                    className="border-b border-[#D2E4D6]/40 hover:bg-[#F9F8F6] cursor-pointer"
                    data-testid={`users-table-row-${u.id}`}
                    onDoubleClick={() => setSelectedUser(u)}
                    title="Double-click to view details"
                  >
                    <td className="px-3 py-2.5 font-medium text-[#000000]">{u.name || "—"}</td>
                    <td className="px-3 py-2.5 text-[#3F3F3F]">{u.email}</td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${ROLE_PILL[u.role] || "bg-[#E4E4E4] text-[#3F3F3F]"}`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[#3F3F3F]">
                      {u.channel_id ? (CHANNEL_LABELS[u.channel_id] || u.channel_id) : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <span
                        className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${STATUS_PILL[u.status] || "bg-[#E4E4E4] text-[#3F3F3F]"}`}
                      >
                        {u.status || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[#3F3F3F]/70 text-xs">
                      {formatCreatedAt(u.created_at)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={resendingId === u.id || u.role === "admin" || u.role === "superadmin"}
                        onClick={(e) => { e.stopPropagation(); resendInvite(u); }}
                        data-testid={`users-table-resend-${u.id}`}
                        className="h-8 text-xs border-[#056839] text-[#056839] hover:bg-[#D2E4D6]"
                      >
                        {resendingId === u.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                            Resend invite
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-6 text-sm text-[#3F3F3F]/60" data-testid="users-table-no-match">
                      No users match your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <UserDetailDialog user={selectedUser} onClose={() => setSelectedUser(null)} />
    </Card>
  );
}


/* ------- User detail dialog (double-click row to open) ------- */
function UserDetailField({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-[#69A488] font-medium">{label}</div>
      <div className="text-sm text-[#3F3F3F] mt-0.5 break-all">{children || <span className="text-[#3F3F3F]/40">—</span>}</div>
    </div>
  );
}

function UserDetailDialog({ user, onClose }) {
  if (!user) return null;
  const fmt = (s) => {
    if (!s) return "—";
    try { return formatDateTime(s); } catch { return s; }
  };
  const Field = UserDetailField;
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle>{user.name || user.email}</DialogTitle>
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${ROLE_PILL[user.role] || "bg-[#E4E4E4] text-[#3F3F3F]"}`}
            >
              {user.role}
            </span>
            <span
              className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-semibold ${STATUS_PILL[user.status] || "bg-[#E4E4E4] text-[#3F3F3F]"}`}
            >
              {user.status || "—"}
            </span>
            <RecordId id={user.id} />
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <Field label="Email">{user.email}</Field>
          <Field label="Phone">{user.phone}</Field>
          <Field label="Channel">{user.channel_id ? (CHANNEL_LABELS[user.channel_id] || user.channel_id) : null}</Field>
          <Field label="Created">{fmt(user.created_at)}</Field>
          <Field label="Last login">{fmt(user.last_login)}</Field>
          <Field label="Must change password">{user.must_change_password ? "Yes" : "No"}</Field>
          <Field label="Address" full>{user.address}</Field>
          {user.role === "facility" && (
            <>
              <Field label="Facility name">{user.facility_name}</Field>
              <Field label="Registration #">{user.registration_number}</Field>
              <Field label="Business registration">{user.business_registration}</Field>
              <Field label="Employees">{user.employees_count}</Field>
              <Field label="Materials processed" full>
                {Array.isArray(user.materials_processed) && user.materials_processed.length > 0
                  ? user.materials_processed.join(", ")
                  : null}
              </Field>
            </>
          )}
          {user.role === "collector" && (
            <Field label="Company" full>{user.company_name}</Field>
          )}
          {user.status === "Rejected" && user.rejection_reason && (
            <Field label="Rejection reason" full>
              <span className="text-[#A0522D]">{user.rejection_reason}</span>
            </Field>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


